import { lazy, Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { TonConnectUIProvider } from "@tonconnect/ui-react";
import { AuthProvider, useAuth } from "./hooks/useAuth";
import { I18nProvider, useI18n } from "./lib/i18n";
import { Shell } from "./components/Shell";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { HomePage } from "./pages/Home";

const ProductPage = lazy(() =>
  import("./pages/Product").then((m) => ({ default: m.ProductPage })),
);
const CountryPage = lazy(() =>
  import("./pages/Country").then((m) => ({ default: m.CountryPage })),
);
const PayPage = lazy(() => import("./pages/Pay").then((m) => ({ default: m.PayPage })));
const OrdersPage = lazy(() =>
  import("./pages/Orders").then((m) => ({ default: m.OrdersPage })),
);
const EsimsPage = lazy(() => import("./pages/Esims").then((m) => ({ default: m.EsimsPage })));
const AccountPage = lazy(() =>
  import("./pages/Account").then((m) => ({ default: m.AccountPage })),
);
const LanguagePage = lazy(() =>
  import("./pages/Language").then((m) => ({ default: m.LanguagePage })),
);
const LegalPage = lazy(() =>
  import("./pages/Legal").then((m) => ({ default: m.LegalPage })),
);
const HelpPage = lazy(() => import("./pages/Help").then((m) => ({ default: m.HelpPage })));
const HelpArticlePage = lazy(() =>
  import("./pages/HelpArticle").then((m) => ({ default: m.HelpArticlePage })),
);

function PageFallback() {
  const { t } = useI18n();
  return (
    <div className="empty">
      <div className="spinner" style={{ margin: "0 auto" }} />
      <p className="muted">{t("loading")}</p>
    </div>
  );
}

const manifestUrl =
  typeof window !== "undefined"
    ? import.meta.env.VITE_TONCONNECT_MANIFEST ||
      `${window.location.origin}/tonconnect-manifest.json`
    : "/tonconnect-manifest.json";

function AppRoutes() {
  return (
    <BrowserRouter>
      <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route element={<Shell />}>
            <Route index element={<HomePage />} />
            <Route path="country/:code" element={<CountryPage />} />
            <Route path="product/:slug" element={<ProductPage />} />
            <Route path="pay/:orderId" element={<PayPage />} />
            <Route path="orders" element={<OrdersPage />} />
            <Route path="esims" element={<EsimsPage />} />
            <Route path="account" element={<AccountPage />} />
            <Route path="account/language" element={<LanguagePage />} />
            <Route path="account/legal/:doc" element={<LegalPage />} />
            <Route path="account/help" element={<HelpPage />} />
            <Route path="account/help/:slug" element={<HelpArticlePage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

function AppWithI18n() {
  const { user } = useAuth();
  return (
    <I18nProvider profileLocale={user?.locale}>
      <AppRoutes />
    </I18nProvider>
  );
}

export default function App() {
  return (
    <ErrorBoundary fallbackTitle="App error">
      <TonConnectUIProvider manifestUrl={manifestUrl}>
        <ErrorBoundary fallbackTitle="UI error">
          <AuthProvider>
            <AppWithI18n />
          </AuthProvider>
        </ErrorBoundary>
      </TonConnectUIProvider>
    </ErrorBoundary>
  );
}
